'use client';
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { INoteBook, INotebookStateCreator } from '../services';
import { IWhitelabelConfig } from '@ui/components/organisms/user/creator-details.typings';
import { useParams, useRouter } from 'next/navigation';
import { Text, Note, Icon } from '@ui/components';
import { ContentItemMenu } from './content-item-menu';
import dayjs from 'dayjs';
import { ContentItemDeleteModal } from './content-delete-modal';
import { PinIcon, Folder } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { DynamicRouteBuilder } from '@ui/constants';
import { getLabel } from '@ui/lib';
import { useMemo, useState } from 'react';

interface ISortableNotebookItemProps {
  notebook: INoteBook;
  state?: INotebookStateCreator;
  userType?: string;
  isAuthenticated?: boolean;
  isFanOfCreator?: boolean;
  creator_id: string;
  whitelabelConfig?: IWhitelabelConfig;
}

export const SortableNotebookItem: React.FC<ISortableNotebookItemProps> = ({
  notebook,
  state,
  userType,
  isAuthenticated,
  isFanOfCreator,
  creator_id,
  whitelabelConfig,
}) => {
  const isPinned = !!notebook.pinned_at;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: notebook.id,
    disabled: isPinned,
  });
  const { creatorSlug } = useParams();
  const router = useRouter();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notebookToDelete, setNotebookToDelete] = useState<string | null>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const handleDelete = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setNotebookToDelete(notebook?.id || null);
    setDeleteModalOpen(true);
  };

  const handleMove = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    state?.onMoveNotebook(notebook);
    e?.view?.close();
    setDropdownOpen(false);
  };

  const handleEdit = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setDropdownOpen(false);
    if (userType === 'creator') {
      router.push(`/dashboard/contents/editor?id=${notebook?.id}`);
    }
  };

  const contentURL = useMemo(() => {
    if (userType === 'creator') {
      return `/dashboard/contents/editor?id=${notebook?.id}`;
    }
    return DynamicRouteBuilder.contentBySlug(
      (creatorSlug as string) || creator_id,
      notebook?.slug || notebook?.id,
    );
  }, [notebook?.id, creator_id, creatorSlug, userType]);

  const dynamicDate = notebook?.date_of_publish ? notebook?.date_of_publish : notebook?.created_at;

  const shouldShowPublishDate = useMemo(() => {
    if (!notebook?.miscellaneous) return true;
    return notebook?.miscellaneous?.showPublishDate ?? true;
  }, [notebook?.miscellaneous]);

  const handlePin = async (e: React.MouseEvent<HTMLDivElement>) => {
    setDropdownOpen(false);
    e.stopPropagation();
    e.preventDefault();
    await state?.handleTogglePin(notebook);
  };

  const shouldShowLockedContentWording = useMemo(() => {
    return isAuthenticated && isFanOfCreator && !notebook.has_access;
  }, [notebook?.has_access, isFanOfCreator, isAuthenticated]);

  const navigateToContent = () => {
    if (isDragging) {
      return;
    }
    router.push(contentURL);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateToContent();
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group self-start">
      {!isPinned && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-2 top-2 z-[100] p-1.5 rounded-md bg-white/90 backdrop-blur-sm border border-gray-200 shadow-sm cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}>
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>
      )}
      <div
        className={twMerge(
          'relative flex min-h-[300px] w-full cursor-pointer flex-col overflow-hidden rounded-md border border-gray-300 bg-white',
          notebook?.pinned_at && 'border-primaryActive',
        )}
        key={notebook?.id}
        role="link"
        tabIndex={isDragging ? -1 : 0}
        onClick={navigateToContent}
        onKeyDown={handleCardKeyDown}>
        <div className="relative w-full shrink-0 rounded-t-md bg-primaryLight/10">
          {notebook?.pinned_at && (
            <div className="absolute right-4 top-[-16px] z-50 flex h-8 w-8 items-center justify-center rounded-full bg-primaryLight">
              <PinIcon className="h-4 w-4 fill-white font-thin text-primaryActive" />
            </div>
          )}
          <div
            className="relative flex w-full items-center justify-center overflow-hidden rounded-t-md"
            style={{ aspectRatio: '4 / 3' }}>
            {notebook?.visibility === 'tiers' && (
              <div className="absolute left-2 top-2 z-50">
                <div className="rounded border border-gray-300/40 bg-white/60 px-2.5 py-0.5 text-[10px] font-semibold text-gray-900 shadow-sm backdrop-blur-md md:text-xs">
                  {getLabel(whitelabelConfig, 'exclusiveContentLabel', 'Exclusive Content')}
                </div>
              </div>
            )}
            {notebook?.visibility === 'fans' && !isAuthenticated && (
              <div className="absolute left-2 top-2 z-50">
                <div className="rounded border border-gray-300/40 bg-white/60 px-2.5 py-0.5 text-[10px] font-semibold text-gray-900 shadow-sm backdrop-blur-md md:text-xs">
                  Free Access
                </div>
              </div>
            )}
            <>
              {notebook?.banner_url && (
                <img
                  src={notebook?.banner_url}
                  alt="banner"
                  className="absolute inset-0 h-full w-full object-cover blur-md"
                />
              )}
              {notebook?.banner_url && (
                <img
                  src={notebook?.banner_url}
                  alt="banner"
                  className="relative z-40 h-full w-full object-contain"
                />
              )}
              {notebook?.children_count != null && notebook?.children_count > 0 && (
                <div className="absolute bottom-2 left-3 z-50 flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-white/30 shadow-lg backdrop-blur-md">
                    <Folder className="h-5 w-5 text-gray-800" />
                  </div>
                  <div className="rounded-full border border-white/40 bg-white/30 px-3 py-1.5 shadow-lg backdrop-blur-md">
                    <Text type="xs" className="font-semibold text-gray-800">
                      {notebook?.children_count} {notebook?.children_count > 1 ? 'items' : 'item'}
                    </Text>
                  </div>
                </div>
              )}
            </>
          </div>

          {userType === 'fan' && shouldShowLockedContentWording && (
            <div className="absolute right-0 top-0 z-40 flex h-full w-full flex-col items-center justify-center space-y-1 rounded-t-md bg-black/50">
              <div className="mt-4 flex max-w-[200px] items-center space-x-1 rounded-2xl border bg-white px-6 py-2 text-sm font-semibold">
                <Icon type="lock" stroke="#000" />
                <span>Unlock content</span>
              </div>
              <div className="text-center text-xs text-white">Available by subscription</div>
            </div>
          )}
        </div>

        {userType === 'creator' && (
          <ContentItemDeleteModal
            open={isDeleteModalOpen}
            setOpen={setDeleteModalOpen}
            notebookId={notebookToDelete}
            state={state!}
          />
        )}

        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-4">
          <div className="flex items-center gap-2">
            <Text
              type="m"
              className="min-w-0 flex-1 text-base font-semibold leading-8 text-gray-800 line-clamp-2">
              {notebook?.name}
            </Text>

            <div
              className="relative z-50 -mr-2 mt-1 shrink-0"
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}>
              <ContentItemMenu
                notebook={notebook}
                userType={userType}
                isOpen={dropdownOpen}
                onOpenChange={setDropdownOpen}
                onEdit={(e) => handleEdit(e)}
                onDelete={(e) => handleDelete(e)}
                onPin={{ handlePin, pinned: !!notebook?.pinned_at }}
                onMove={(e) => handleMove(e)}
              />
            </div>
          </div>

          <Text type="m" className="mt-1 line-clamp-2 text-xs leading-5 text-note">
            {notebook?.description}
          </Text>

          <div className="mt-auto pt-2">
            <Note type="xs" className="text-xs font-light">
              {notebook?.date_of_publish
                ? 'Published'
                : userType === 'creator'
                ? 'Draft, created'
                : 'Created'}{' '}
              {dayjs(dynamicDate).format('DD MMM')}
            </Note>
          </div>
        </div>
      </div>
    </div>
  );
};
